import styled from '@emotion/styled'
import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Switch,
  Typography,
  Upload,
  message,
} from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { FC, useEffect, useState } from 'react'
import { allowFileExtensionsImage } from 'config'
import { useCreateGeneralStock, useUpdateGeneralStock } from 'hooks/useGeneralStock'
import { uploadImage } from 'services/factoryAPI'
import type { GeneralStockFormEdit } from 'services/interface/generalStock.interface'
import { numberFormatInt } from 'utils/nuberFormat'
import { createInputNumberGuards } from 'utils'
import { MASTER_STOCK_LABEL } from '../Recipe/bundleLabels'
import StockImageThumb from '../StockImageThumb'
import { formatGeneralStockDisplayName } from '../inventoryHelpers'

export interface GeneralStockFormValues {
  name?: string
  description?: string
  quantity?: number
  remark?: string
  is_active?: boolean
}

interface GeneralStockFormModalProps {
  open: boolean
  onClose: () => void
  /** มีค่า = โหมดแก้ไข */
  editing?: GeneralStockFormEdit | null
}

const extractImageIds = (fileList: UploadFile[]): string[] =>
  fileList
    .map((f) => (f.response?.id as string) ?? (f as UploadFile & { id?: string }).id)
    .filter((id): id is string => !!id)

const buildFileListFromIds = (
  ids?: string[] | null,
  urls?: string[] | null
): UploadFile[] =>
  (ids ?? []).map((id, index) => ({
    uid: id,
    name: `image-${index + 1}`,
    status: 'done',
    id,
    url: urls?.[index],
  }))

const GeneralStockFormModal: FC<GeneralStockFormModalProps> = ({
  open,
  onClose,
  editing,
}) => {
  const [form] = Form.useForm<GeneralStockFormValues>()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const isEdit = !!editing?.id
  // จำนวนสต็อกรับได้เฉพาะจำนวนเต็ม
  const integerGuards = createInputNumberGuards({ mode: 'integer' })

  const { mutate: createMutate, isPending: creating } = useCreateGeneralStock()
  const { mutate: updateMutate, isPending: updating } = useUpdateGeneralStock()
  const isSaving = creating || updating

  useEffect(() => {
    if (!open) return
    if (editing) {
      form.setFieldsValue({
        name: editing.name ?? undefined,
        description: editing.description ?? undefined,
        remark: editing.remark ?? undefined,
        is_active: editing.is_active !== false,
      })
      setFileList(buildFileListFromIds(editing.images, editing.image_urls))
    } else {
      form.resetFields()
      form.setFieldsValue({
        quantity: 0,
        is_active: true,
      })
      setFileList([])
    }
  }, [open, editing, form])

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const images = extractImageIds(fileList)

      if (isEdit && editing) {
        updateMutate(
          {
            id: editing.id,
            name: values.name?.trim() || undefined,
            description: values.description?.trim() || undefined,
            images: images.length > 0 ? images : undefined,
            remark: values.remark?.trim() || undefined,
            is_active: values.is_active !== false,
          },
          {
            onSuccess: () => {
              message.success('บันทึกข้อมูลทั่วไปแล้ว')
              onClose()
            },
            onError: (err) => message.error(err.message || 'บันทึกไม่สำเร็จ'),
          }
        )
        return
      }

      createMutate(
        {
          name: values.name?.trim() || undefined,
          description: values.description?.trim() || undefined,
          images: images.length > 0 ? images : undefined,
          quantity: values.quantity ?? 0,
          remark: values.remark?.trim() || undefined,
          is_active: values.is_active !== false,
        },
        {
          onSuccess: () => {
            message.success(`เพิ่ม${MASTER_STOCK_LABEL}แล้ว`)
            onClose()
          },
          onError: (err) => message.error(err.message || 'เพิ่มไม่สำเร็จ'),
        }
      )
    })
  }

  const displayName = editing
    ? formatGeneralStockDisplayName(editing.name, editing.description)
    : ''

  const stockQty = editing?.quantity ?? 0
  const stockHold = editing?.holding_quantity ?? 0
  const stockBal = editing?.stock_balance ?? stockQty - stockHold

  return (
    <Modal
      title={
        isEdit ? (
          <TitleBlock>
            <Typography.Text strong style={{ fontSize: 16 }}>
              แก้ไขข้อมูลทั่วไป
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {MASTER_STOCK_LABEL}
            </Typography.Text>
          </TitleBlock>
        ) : (
          `เพิ่ม${MASTER_STOCK_LABEL}`
        )
      }
      open={open}
      onCancel={onClose}
      footer={
        <FooterRow>
          <Button onClick={onClose} disabled={isSaving}>
            ยกเลิก
          </Button>
          <Button type="primary" loading={isSaving} onClick={handleSubmit}>
            {isEdit ? 'บันทึกข้อมูล' : 'สร้างรายการ'}
          </Button>
        </FooterRow>
      }
      destroyOnClose
      centered
      width={840}
      bodyStyle={{
        paddingTop: 8,
        paddingBottom: 16,
        maxHeight: '75vh',
        overflowY: 'auto',
      }}
    >
      {isEdit && editing && (
        <EditHeader>
          <EditThumb>
            <StockImageThumb
              src={editing.image_urls?.[0]}
              alt={displayName}
            />
          </EditThumb>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Typography.Text strong style={{ fontSize: 15, display: 'block' }}>
              {displayName || '—'}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              แก้ไขได้เฉพาะชื่อ รายละเอียด รูปภาพ และสถานะ — ยอดสต็อกปรับจากเมนู &quot;ปรับสต็อก&quot;
            </Typography.Text>
          </div>
        </EditHeader>
      )}

      <Form form={form} layout="vertical" requiredMark={false} disabled={isSaving}>
        <Row gutter={28}>
          {/* ── ซ้าย: รูปภาพ + สถานะ ── */}
          <Col xs={24} md={10}>
            <SectionLabel>รูปภาพ</SectionLabel>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
            >
              อัปโหลดได้สูงสุด 5 รูป
            </Typography.Text>
            <Form.Item label={null} colon={false} style={{ marginBottom: 16 }}>
              <UploadWrap>
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  accept={allowFileExtensionsImage}
                  maxCount={5}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    try {
                      const result = await uploadImage(file as File)
                      onSuccess?.(result)
                    } catch (e) {
                      onError?.(e as Error)
                    }
                  }}
                  onChange={({ fileList: next }) => {
                    const mapped = next.map((item) => {
                      if (item.response) {
                        return {
                          ...item,
                          id: item.response.id,
                          url: item.response.url,
                        }
                      }
                      return item
                    })
                    setFileList(mapped)
                  }}
                >
                  {fileList.length >= 5 ? null : (
                    <UploadTrigger>
                      <PlusOutlined style={{ fontSize: 20 }} />
                      <span>อัปโหลด</span>
                    </UploadTrigger>
                  )}
                </Upload>
              </UploadWrap>
            </Form.Item>

            <Divider style={{ margin: '4px 0 16px' }} />
            <ActiveRow>
              <div>
                <SectionLabel style={{ marginBottom: 4 }}>สถานะรายการ</SectionLabel>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  ปิดใช้งานแล้วรายการจะไม่แสดงในการเลือกไอเท็ม
                </Typography.Text>
              </div>
              <Form.Item name="is_active" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
              </Form.Item>
            </ActiveRow>
          </Col>

          {/* ── ขวา: ข้อมูลหลัก + สต็อก ── */}
          <Col xs={24} md={14}>
            <SectionLabel>ข้อมูลหลัก</SectionLabel>
            <Form.Item
              label="ชื่อรายการ"
              name="name"
              rules={[{ required: true, message: 'กรุณากรอกชื่อรายการ' }]}
            >
              <Input placeholder="เช่น ไอเท็ม A, บรรจุภัณฑ์" maxLength={255} />
            </Form.Item>

            <Form.Item label="รายละเอียด" name="description">
              <Input.TextArea
                rows={3}
                placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)"
                style={{ resize: 'vertical' }}
              />
            </Form.Item>

            <Form.Item label="หมายเหตุภายใน" name="remark">
              <Input placeholder="บันทึกภายในโรงงาน (ไม่แสดงต่อลูกค้า)" />
            </Form.Item>

            {!isEdit && (
              <Form.Item
                label="จำนวนเริ่มต้น (ชิ้น)"
                name="quantity"
                extra="กำหนดได้ครั้งเดียวตอนสร้าง หลังจากนั้นปรับผ่านปุ่ม «ปรับสต็อก» ในตารางคลัง"
              >
                <InputNumber
                  min={0}
                  precision={0}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="0"
                  {...integerGuards}
                />
              </Form.Item>
            )}

            {isEdit && (
              <>
                <SectionLabel>สต็อก (อ่านอย่างเดียว)</SectionLabel>
                <StockSnapshot>
                  <StockSnapshotItem>
                    <StockSnapshotLabel>คงเหลือ</StockSnapshotLabel>
                    <StockSnapshotValue>{numberFormatInt(stockQty)}</StockSnapshotValue>
                  </StockSnapshotItem>
                  <StockSnapshotItem>
                    <StockSnapshotLabel>กำลังมีคนซื้อ</StockSnapshotLabel>
                    <StockSnapshotValue $tone="hold">{numberFormatInt(stockHold)}</StockSnapshotValue>
                  </StockSnapshotItem>
                  <StockSnapshotItem>
                    <StockSnapshotLabel>พร้อมขาย</StockSnapshotLabel>
                    <StockSnapshotValue $tone="balance" $positive={stockBal > 0}>
                      {numberFormatInt(stockBal)}
                    </StockSnapshotValue>
                  </StockSnapshotItem>
                </StockSnapshot>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  ปรับยอดผ่านปุ่ม «ปรับสต็อก» / «ประวัติ» ในตารางคลัง
                </Typography.Text>
              </>
            )}
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

export default GeneralStockFormModal

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const EditHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #f0f7ff 0%, #fafafa 100%);
  border: 1px solid #e6f4ff;
  border-radius: 10px;
`

const EditThumb = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  background: #fff;
`

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
  letter-spacing: 0.01em;
`

const StockSnapshot = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`

const StockSnapshotItem = styled.div`
  flex: 1;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  text-align: center;
`

const StockSnapshotLabel = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  margin-bottom: 4px;
`

const StockSnapshotValue = styled.div<{
  $tone?: 'default' | 'hold' | 'balance'
  $positive?: boolean
}>`
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone, $positive }) => {
    if ($tone === 'hold') return '#d46b08'
    if ($tone === 'balance') return $positive ? '#389e0d' : '#cf1322'
    return '#262626'
  }};
`

const ActiveRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`

const UploadWrap = styled.div`
  .ant-upload-list.ant-upload-list-picture-card {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .ant-upload.ant-upload-select {
    width: 104px !important;
    height: 104px !important;
    margin: 0 !important;
  }
  .ant-upload-list-item-container {
    width: 104px !important;
    height: 104px !important;
    margin: 0 !important;
  }
`

const UploadTrigger = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #1677ff;
  font-size: 12px;
`
